import React from 'react';
import { Coins, AlertTriangle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface TokenConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tokenCount: number | null;
  isLoading: boolean;
}

const TokenConfirmationModal: React.FC<TokenConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  tokenCount,
  isLoading
}) => {
  const { t } = useLanguage();
  
  if (!isOpen) return null;

  const hasEnoughTokens = tokenCount !== null && tokenCount > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true" onClick={onClose}></div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                hasEnoughTokens ? 'bg-green-100' : 'bg-yellow-100'
              }`}>
                {hasEnoughTokens ? (
                  <CheckCircle className="w-6 h-6 text-green-600" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-yellow-600" aria-hidden="true" />
                )}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                  {hasEnoughTokens ? t('tokens.confirmationModal.title') : t('tokens.confirmationModal.insufficientTitle')}
                </h3>
                <div className="mt-2">
                  {hasEnoughTokens ? (
                    <>
                      <p className="text-sm text-gray-500">
                        {t('tokens.confirmationModal.useToken')} <span className="font-semibold text-primary">{tokenCount} {tokenCount === 1 ? t('tokens.token') : t('tokens.available')}</span>.
                      </p>
                      <div className="flex items-center mt-4 p-3 bg-blue-50 rounded-md">
                        <Coins className="mr-2 h-5 w-5 text-blue-500" />
                        <p className="text-sm text-blue-700">
                          {t('tokens.confirmationModal.pdfsIncluded')}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500">
                        {t('tokens.confirmationModal.noTokens')}
                      </p>
                      <p className="mt-2 text-sm text-gray-500">
                        {t('tokens.confirmationModal.buyMore')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
            {hasEnoughTokens ? (
              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm ${
                  isLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <>
                    <svg className="w-5 h-5 mr-2 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('tokens.confirmationModal.processing')}
                  </>
                ) : (
                  t('tokens.confirmationModal.confirm')
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              >
                {t('tokens.confirmationModal.buy')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('tokens.confirmationModal.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenConfirmationModal;